import { debounceTime } from "rxjs/operators";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ElementRef,
  AfterViewInit,
  OnDestroy,
  Component,
  Input,
  HostListener,
  ViewChild,
} from "@angular/core";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import {
  ScratchPadTableDefinitionFactory,
  TableDefinition,
  IndexType,
  SystemConfigPromiseService,
  COLUMN_ORDER_STATE,
  OrderDataLookupService,
  GenericTooltipValueGetter,
} from "shared/cubiq-services";
import { GridOptions } from "ag-grid-community/main";
import { Subscription, Subject } from "rxjs";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxScratchPadService } from "./services/bfx-scratchpad.service";
import { COLUMN_ORDER_ID } from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { COLUMN_EXTERNAL_REF } from "shared/cubiq-services/model/ScratchPadTableDefinitionFactory.constants";
import { DashboardItemType } from "../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../golden-layout/golden-layout-addition-mode";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { ScratchPadOrder } from "shared/cubiq-services/model/orders/ScratchPadOrder";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { BfxBaseBlotter } from "app/bfx-base-blotter/bfx-base-blotter";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { Rect } from "shared/native-desktop/NativeDesktopData";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
import { MultiAllocProductRenderer } from "app/bfx-blotter-common/renderers/MultiAllocProductRenderer";
import { DETAILS_MIN_SIZE } from "shared/cubiq-services/model/golden-layout.constants";

const ACCEPTANCE_STATES: CubiqTS.OrderState[] = [
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
];
const APPROVAL_STATES: CubiqTS.OrderState[] = [CubiqTS.OrderState.DI_NEEDED];
const REJECT_STATES: CubiqTS.OrderState[] = ACCEPTANCE_STATES.concat(APPROVAL_STATES);

export const DEFAULT_ROW_HEIGHT: number = 24;

@Component({
  selector: "bfx-scratchpad",
  templateUrl: "./html/bfx-scratchpad.html",
  standalone: false,
})
export class BfxScratchPadComponent implements OnDestroy, GoldenLayoutComponent, AfterViewInit {
  public frameworkComponents: any = {
    productRenderer: MultiAllocProductRenderer,
  };
  public static instanceId = 0;
  public blotterId: string;
  public rowHeight = DEFAULT_ROW_HEIGHT;
  public height: number = 500;
  protected primed: boolean = false;
  protected tableData: any = null;
  public restored: boolean = false;
  @Input("columnsConfig")
  public columnsConfiguration: any = {};
  public blotterInstance: any = null;
  public tableDefinition: TableDefinition = null;
  public columnDefs: Array<BfxColDef> = [];
  public gridOptions: GridOptions;
  public blotterView: ElementRef;
  private fullUpdateSubscription: Subscription;
  protected deleteSubscription: Subscription;
  private doingDragSelect: boolean = false;
  private firstRowToSelect: any;
  private lastRowToSelect: any;
  private selectedIdsBeforeDrag: Array<string> = [];
  protected suppressNextFilterChangedDeselection = false;
  public importBusy: boolean = false;
  public sortingModel: any = null;

  private documentListener: () => void;

  @ViewChild("cm", { static: true })
  public contextMenuElement: ElementRef;

  public canEdit: boolean = false;
  public canCopy: boolean = false;
  public canDelete: boolean = false;
  public canSave: boolean = false;
  public canSubmit: boolean = false;
  public canView: boolean = false;
  public canAccept: boolean = false;
  public canApprove: boolean = false;
  public canReject: boolean = false;
  public canAbort: boolean = false;
  public canBulk: boolean = false;
  public canUnbulk: boolean = false;
  public canDropFile: boolean = false;

  public showMenu: boolean = false;
  public menuPosition: any = { x: 0, y: 0 };

  private subjectCheckPermissions: Subject<void> = new Subject<void>();
  private subjectRefresh: Subject<void> = new Subject<void>();
  private myHeaderSelectionTimer: any;
  private subjectRefreshHeader: Subject<void> = new Subject<void>();
  private menuWindow: NativeDesktopWindow;
  protected partialUpdateSub: Subscription;

  constructor(
    BfxBlotterElement: ElementRef,
    private _tableDefinitionFactory: ScratchPadTableDefinitionFactory,
    protected layoutService: GoldenLayoutService,
    protected translationService: TranslateService,
    protected orderDataService: OrderDataLookupService,
    protected sysConfigService: SystemConfigPromiseService,
    protected mouseModifierKeyService: BfxMouseModifierKeyService,
    private scratchPadService: BfxScratchPadService,
    private element: ElementRef,
  ) {
    (this.constructor as any).instanceId++;
    this.blotterId = this.constructor.name; // + "_" + (this.constructor as any).instanceId;
    this.blotterInstance = this;
    this.blotterView = BfxBlotterElement;

    this.subjectCheckPermissions
      .pipe(debounceTime(100))
      .subscribe(this.checkActionPermissions.bind(this));
    this.subjectRefresh.pipe(debounceTime(100)).subscribe(this.refresh.bind(this));
    this.subjectRefreshHeader
      .pipe(debounceTime(100))
      .subscribe(this.updateSelectionHeader.bind(this));

    this.gridOptions = <GridOptions>{
      getRowNodeId: this.getRowNodeId.bind(this),
      rowSelection: "multiple",
      deltaRowDataMode: true,
      valueCache: true,
      alignedGrids: [],
      headerHeight: 24,
      stopEditingWhenGridLosesFocus: true,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressTabbing: false,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: false,
      treeData: true,
      rowDeselection: true,
      rowMultiSelectWithClick: false,
      groupSuppressAutoColumn: true,
      enableBrowserTooltips: true,
      getRowHeight: function (params) {
        return DEFAULT_ROW_HEIGHT;
      },

      onGridReady: (params) => {
        if (this.gridOptions != null) {
          this.gridOptions.api = params.api;
          this.gridOptions.columnApi = params.columnApi;
        }

        this._tableDefinitionFactory.defineTable().then(
          (value) => {
            this.tableDefinition = value;
            this.initializeColumnDefinitions();
            this.gridOptions.api.hideOverlay();
            this.initializeInMemory();
          },
          (reason: string) => {
            logger.out.info(
              "RawDataScartchPadAdapter:_tableDefinitionFactory.defineTable rejection: " + reason,
            );
          },
        );
      },
      onCellMouseOver: (event) => {
        setImmediate(() => {
          if (this.doingDragSelect) {
            if (event.event["buttons"] == 0) {
              this.doingDragSelect = false;
            } else {
              this.lastRowToSelect = event.rowIndex;
              // event.node.setSelected(true);
              this.gridOptions.api.deselectAll();
              this.selectedIdsBeforeDrag.forEach((element) => {
                let row = this.gridOptions.api.getRowNode(element);
                if (row && row.isSelected() == false) {
                  row.setSelected(true, false, true);
                }
              });
              for (
                let index = Math.min(this.firstRowToSelect, this.lastRowToSelect);
                index <= Math.max(this.firstRowToSelect, this.lastRowToSelect);
                index++
              ) {
                let row = this.gridOptions.api.getDisplayedRowAtIndex(index);
                if (row && (row.isSelected() == false || index === event.rowIndex)) {
                  row.setSelected(true, false, index !== event.rowIndex);
                }
                // this.gridOptions.api.selectIndex(index,true,false);
              }
            }
          }
        });
      },
      onCellMouseDown: (event) => {
        setImmediate(() => {
          this.doingDragSelect = true;

          this.firstRowToSelect = event.rowIndex;
          this.lastRowToSelect = event.rowIndex;

          // There is no need to listen to each document for a mouse event if we have it here
          if (NativeDesktopSupport.isNativeDesktopContainer() && event?.event != null) {
            this.mouseModifierKeyService.onCtrlMouseDown(event.event);
          }

          if (
            event.node.isSelected() &&
            this.selectedRowsLength() === 1 &&
            !this.mouseModifierKeyService.ctrlKey() &&
            !this.mouseModifierKeyService.shiftKey() &&
            this.mouseModifierKeyService.isMouseLeft() &&
            !this.mouseModifierKeyService.isTargetCheckbox()
          ) {
            // setTimeout((node)=>{
            // 	node.setSelected(false);
            // },200,event.node);
          } else if (
            !this.mouseModifierKeyService.ctrlKey() &&
            !this.mouseModifierKeyService.shiftKey() &&
            this.mouseModifierKeyService.isMouseLeft() &&
            !this.mouseModifierKeyService.isTargetCheckbox() &&
            !this.mouseModifierKeyService.isTargetDownCheckbox()
          ) {
            this.gridOptions.api.deselectAll();
          }

          this.selectedIdsBeforeDrag = [];
          this.gridOptions.api.getSelectedNodes().forEach((element) => {
            this.selectedIdsBeforeDrag.push(element.id);
          });
        });
      },

      onCellContextMenu: (event) => {
        if (this.gridOptions.api) {
          if (!event.node.isSelected()) {
            this.gridOptions.api.deselectAll();
            event.node.setSelected(true, false);
          }
          let selectedItems = this.gridOptions.api.getSelectedRows();
          let selectedIds: Array<string> = [];

          selectedItems.map((order) => {
            selectedIds.push(order[this.tableDefinition.primaryKey]);
          });
          let mouseEvent: MouseEvent = event.event as MouseEvent;
          this.handleShowMenu(mouseEvent);
        }
      },

      getDataPath: (data) => {
        return data.key.split("-");
      },

      context: {
        isNegativeMarkup: this.scratchPadService.isNegativeMarkup.bind(this.scratchPadService),
        isFailedFatFingers: this.scratchPadService.isFailedFatFingers.bind(this.scratchPadService),
        isPending: this.scratchPadService.isPending.bind(this.scratchPadService),
        getIcon: this.scratchPadService.getIconData.bind(this.scratchPadService),
      },
    };

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.blotterView.nativeElement);
  }

  ngAfterViewInit() {
    let title: string = "contextMenu-scratchpad";
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      NativeDesktopSupport.openWindow(
        title,
        WindowType.Utility,
        true,
        null,
        (window: NativeDesktopWindow) => {
          window["beforeClose"] = () => {
            window.hide();
            return false;
          };

          //this.showMenu = true;
          this.menuWindow = window;
          this.menuWindow.attachToParentElement(this.blotterView.nativeElement);
        },
      );
    }
  }

  protected initializeColumnDefinitions(): void {
    (this.gridOptions.onSelectionChanged = () => {
      this.subjectRefreshHeader.next();
      BfxBaseBlotter.gridSelectionChanged(this.gridOptions);
    }),
      this.applyColumnDefinitions();
  }

  protected checkActionPermissions(): void {
    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();

      this.canCopy = rows.length > 0;
      this.canSave =
        rows.length > 0 &&
        rows.filter(
          (row) =>
            row[COLUMN_ORDER_ID] == null &&
            !this.scratchPadService.isNegativeMarkup(row[COLUMN_EXTERNAL_REF]) &&
            !this.scratchPadService.isFailedFatFingers(row[COLUMN_EXTERNAL_REF]) &&
            !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]),
        ).length == rows.length;
      this.canSubmit =
        rows.length > 0 &&
        rows.filter(
          (row) =>
            (row[COLUMN_ORDER_ID] == null ||
              row[COLUMN_ORDER_STATE] == CubiqTS.OrderState.SAVED ||
              row[COLUMN_ORDER_STATE] == CubiqTS.OrderState.REJECTED) &&
            !this.scratchPadService.isNegativeMarkup(row[COLUMN_EXTERNAL_REF]) &&
            !this.scratchPadService.isFailedFatFingers(row[COLUMN_EXTERNAL_REF]) &&
            !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]),
        ).length == rows.length;
      this.canEdit =
        rows.length == 1 &&
        rows.filter(
          (row) =>
            (row[COLUMN_ORDER_ID] == null ||
              row[COLUMN_ORDER_STATE] == CubiqTS.OrderState.SAVED ||
              row[COLUMN_ORDER_STATE] == CubiqTS.OrderState.REJECTED) &&
            !this.scratchPadService.isNegativeMarkup(row[COLUMN_EXTERNAL_REF]) &&
            !this.scratchPadService.isFailedFatFingers(row[COLUMN_EXTERNAL_REF]) &&
            !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]),
        ).length == rows.length;
      this.canView =
        rows.length == 1 &&
        rows[0][COLUMN_ORDER_ID] != "-" &&
        rows[0][COLUMN_ORDER_ID] != null &&
        rows[0][COLUMN_ORDER_ID] != "" &&
        rows[0][COLUMN_ORDER_STATE] != CubiqTS.OrderState.REJECTED;
      this.canDelete = this.canSubmit; // Revise once you have submit. Processing orders need a marker;
      this.canAccept =
        rows.length > 0 &&
        rows.filter(
          (row) =>
            !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]) &&
            (this.scratchPadService.isNegativeMarkup(row[COLUMN_EXTERNAL_REF]) ||
              this.scratchPadService.isFailedFatFingers(row[COLUMN_EXTERNAL_REF]) ||
              this.acceptAllowed(row)),
        ).length == rows.length;
      this.canApprove =
        rows.length > 0 && rows.filter((row) => this.approveAllowed(row)).length == rows.length;
      this.canReject =
        (this.canAccept || this.canApprove) &&
        rows.length > 0 &&
        rows.filter(
          (row) =>
            !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]) &&
            (this.scratchPadService.isNegativeMarkup(row[COLUMN_EXTERNAL_REF]) ||
              this.scratchPadService.isFailedFatFingers(row[COLUMN_EXTERNAL_REF]) ||
              this.rejectAllowed(row)),
        ).length == rows.length;
      this.canAbort =
        rows.length > 0 && rows.filter((row) => this.abortAllowed(row)).length == rows.length;
      this.canUnbulk =
        rows.length > 0 &&
        rows.filter((row: ScratchPadOrder) => row.multiAllocation === true && row.orderId == null)
          .length == rows.length;
      this.canBulk = this.bulkAllowed(rows);
    }
  }

  protected getRowNodeId(data: ScratchPadOrder): string {
    return data.externalClientReference;
  }

  private acceptAllowed(row: any): boolean {
    let allowed: boolean = ACCEPTANCE_STATES.indexOf(row[COLUMN_ORDER_STATE]) >= 0;
    return allowed && this.scratchPadService.canAccept(row);
  }

  private approveAllowed(row: any): boolean {
    let allowed: boolean =
      APPROVAL_STATES.indexOf(row[COLUMN_ORDER_STATE]) >= 0 &&
      !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]);
    return allowed && this.scratchPadService.canApprove(row);
  }

  private rejectAllowed(row: any): boolean {
    let allowed: boolean = REJECT_STATES.indexOf(row[COLUMN_ORDER_STATE]) >= 0;
    return allowed && this.scratchPadService.canReject(row);
  }

  private abortAllowed(row: any): boolean {
    let allowed: boolean =
      REJECT_STATES.indexOf(row[COLUMN_ORDER_STATE]) >= 0 &&
      !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]);
    return allowed && this.scratchPadService.canAbort(row);
  }

  private bulkAllowed(orders: ScratchPadOrder[]): boolean {
    return this.scratchPadService.canBulkOrders(orders);
  }

  protected applyColumnDefinition(column: BfxColDef): any {
    let col: BfxColDef = Object.assign({}, column);
    let colConfig = null;
    if (this.columnsConfiguration) colConfig = this.columnsConfiguration[col.field];

    if (column.searchKey != null) {
      col.getQuickFilterText = function (params) {
        let myRetrun = column.hasSearchKey ? params.data[column.searchKey] : "";
        return myRetrun;
      };
    }

    if (column.minWidth != null) {
      col.minWidth = column.minWidth;
    }
    if (column.width != null) {
      if (colConfig != null && colConfig != undefined && colConfig.width != undefined) {
        col.width = colConfig.width;
      } else {
        col.width = column.width;
      }
    }
    if (col.minWidth > 0 && col.width < col.minWidth) {
      col.width = col.minWidth;
    }

    col.position = column.position;

    BfxBaseBlotter.applySelectionCellRules(col);

    this.applyTooltipValueGetter(col);

    col.suppressKeyboardEvent = function (event) {
      if (event && event.event && (event.event.code == "Tab" || event.event.code == "Enter")) {
        return false;
      }
      return event.editing;
    };

    this.applyComparator(col, column);

    return col;
  }

  protected applyComparator(col: any, column: BfxColDef): void {
    let columnName: string = column.sortKey ? column.sortKey : column.field;

    if (col.comparator == null) {
      switch (column.indexType) {
        case IndexType.Boolean:
        case IndexType.String:
          col.comparator = this.sortString.bind(this, columnName);
          break;
        case IndexType.Double:
        case IndexType.Integer:
          col.comparator = this.sortNumber.bind(this, columnName);
          break;
        case IndexType.Long:
          col.comparator = this.sortLong.bind(this, columnName);
          break;
        case IndexType.NaturalString:
          col.comparator = this.sortNumber.bind(this, columnName);
          break;
        case IndexType.AgGridDefault:
        default:
          break;
      }
    }
  }

  protected sortString(columnName: string, valueA: string, valueB: string, nodeA, nodeB): number {
    let strA: string = valueA;
    if (!strA) strA = "";
    let strB: string = valueB;
    if (!strB) strB = "";
    return strA.localeCompare(strB);
  }

  protected sortNumber(columnName: string, valueA: number, valueB: number, nodeA, nodeB): number {
    return Number(nodeA.data[columnName]) - Number(nodeB.data[columnName]);
  }

  protected sortLong(
    columnName: string,
    valueA: CubiqTS.Long,
    valueB: CubiqTS.Long,
    nodeA,
    nodeB,
  ): number {
    return nodeA.data[columnName].compare(nodeB.data[columnName]);
  }

  protected initializeInMemory(): void {
    this.scratchPadService.isReady().then(() => {
      let rowData: Array<any> = this.scratchPadService.getRowData();
      this.gridOptions.api.setRowData(rowData);
    });
    this.fullUpdateSubscription = this.scratchPadService.fullUpdateSubject.subscribe(
      this.updateGridData.bind(this),
    );
    this.deleteSubscription = this.scratchPadService.deleteItemSubject.subscribe(
      this.updateGridData.bind(this),
    );
    this.partialUpdateSub = this.scratchPadService.partialUpdateSubject.subscribe(
      this.onPartialUpdateEvent.bind(this),
    );
    setTimeout(() => {
      this.restoreSortFromConfig();
    }, 0);
  }

  protected applyColumnDefinitions(): void {
    let defs: Array<BfxColDef> = this.tableDefinition.columns.concat();
    defs = defs.map((def) => {
      return this.applyColumnDefinition(def);
    });
    this.columnDefs = defs;
  }

  ngOnDestroy() {
    if (this.fullUpdateSubscription) {
      this.fullUpdateSubscription.unsubscribe();
    }
    if (this.partialUpdateSub) this.partialUpdateSub.unsubscribe();

    if (typeof this.documentListener === "function") {
      this.documentListener();
    }

    if (this.menuWindow) {
      this.menuWindow.getNativeWindow().close();
      this.menuWindow = null;
    }
  }

  resize() {
    // NO OP
  }

  public getComponentConfiguration(): any {
    return {
      blotterId: this.blotterId,
      columnsConfiguration: this.getBlotterColumnsConfigs(),
      sortingModel: this.getBlotterSortingModel(),
    };
  }

  public getBlotterId(): string {
    return this.blotterId;
  }

  private getBlotterSortingModel() {
    if (
      this.gridOptions == null ||
      this.gridOptions.api == null ||
      typeof this.gridOptions.api.getSortModel !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // sort model won't be restored but defaults will still apply
      return null;
    }

    return this.gridOptions.api.getSortModel();
  }

  public getBlotterColumnsConfigs(): any {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // column config won't be restored but defaults will still apply
      return null;
    }

    let columnsToSave = {};
    let positionCounter = 0;
    this.gridOptions.columnApi.getAllGridColumns().forEach((gridCol) => {
      let column = {
        id: "",
        width: 150,
        position: 1,
        visible: true,
      };
      column.id = gridCol.getColId();
      column.width = gridCol.getActualWidth();
      column.visible = gridCol.isVisible();
      column.position = positionCounter;

      columnsToSave[gridCol.getColId()] = column;
      positionCounter++;
    });
    this.columnsConfiguration = columnsToSave;
    return columnsToSave;
  }

  protected selectedRowsLength(): number {
    return this.gridOptions.api.getSelectedRows().length;
  }

  public resizeScrollBarForData() {
    // NO OP
  }

  private updateGridData(sync: boolean = false): void {
    if (sync == true) {
      this.checkActionPermissions();
      this.refresh();
      this.updateSelectionHeader();
    } else {
      this.subjectRefresh.next();
      this.subjectCheckPermissions.next();
      this.subjectRefreshHeader.next();
    }
  }

  private refresh(): void {
    if (this.tableDefinition && this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.setRowData(this.scratchPadService.getRowData());
    } else {
      logger.out.warn(`${this.constructor.name}.refresh() failed`);
    }
  }

  //buttons handlers

  private hideMenu(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer() && this.menuWindow != null) {
      this.menuWindow.hide();
    }
  }

  public handleSave() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      rows = rows.filter(
        (row) =>
          row[COLUMN_ORDER_ID] == null &&
          !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]),
      );
      if (rows.length > 0) {
        this.hideActions();
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService.saveItems(orderIds).then((response: any) => {
          this.updateGridData();
        });
      }
    }
  }

  public handleEdit() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length == 1) {
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService.edit(orderIds[0]);
      }
    }
  }

  public handleCopy() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService
          .cloneOrderRequests(orderIds)
          .then((value: Array<CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl>) => {
            this.updateGridData(true);
            setTimeout(
              (() => {
                value.forEach(
                  (
                    row: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl,
                    index: number,
                  ) =>
                    this.gridOptions.api
                      .getRowNode(
                        row instanceof CubiqTS.FixingOrderRequestImpl
                          ? (row as CubiqTS.FixingOrderRequestImpl).fixingOrderData
                              .externalClientReference
                          : row.linkReference,
                      )
                      .setSelected(true, index == 0),
                );
              }).bind(this),
              200,
            );
          });
      }
    }
  }

  public handleDelete() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        let orderIdsToDelete: Array<string> = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        if (orderIdsToDelete.length > 0) {
          this.scratchPadService.deleteOrders(orderIdsToDelete).then(
            ((value: any) => {
              this.updateGridData();
            }).bind(this),
          );
        }
      }
    }
  }

  public handleSubmit() {
    this.hideMenu();

    this.scratchPadService.notifyImportBusyOnFooter("footer.notification.scratchpad.processing");
    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      rows = rows.filter(
        (row) =>
          (row[COLUMN_ORDER_STATE] == CubiqTS.OrderState.SAVED ||
            row[COLUMN_ORDER_STATE] == CubiqTS.OrderState.REJECTED ||
            row[COLUMN_ORDER_ID] == null) &&
          !this.scratchPadService.isPending(row[COLUMN_EXTERNAL_REF]),
      );
      if (rows.length > 0) {
        this.hideActions();
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService.submitOrders(orderIds).then((response: Array<any>) => {
          logger.out.info("Response ", response);
          this.updateGridData();
        });
      }
    }
  }

  public handleView() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: ScratchPadOrder[] = this.gridOptions.api.getSelectedRows();
      let config: GLPopoutConfiguration = new GLPopoutConfiguration();
      let selectedId = rows[0][COLUMN_ORDER_ID];
      let selectedRef = rows[0]["orderRef"];
      config.initialState = GLPopoutState.POPOUT;
      config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
      config.popoutResizable = false;
      config.minimumSize = DETAILS_MIN_SIZE;

      config.data = {};

      // don't persist selectedIds in config
      Object.defineProperty(config.data, "selectedIds", {
        enumerable: false,
        writable: true,
      });

      config.data.selectedIds = [selectedId];

      let title: string = this.translationService.instant("order.details.title", [selectedRef]);
      this.layoutService.launchFromDashboard(
        title,
        DashboardItemType.OrderDetails,
        true,
        GoldenLayoutAdditionMode.DEFAULT,
        config,
      );
    }
  }

  public handleShowMenu(event): void {
    if (!NativeDesktopSupport.isNativeDesktopContainer()) {
      const viewElement = this.contextMenuElement.nativeElement?.nativeElement;
      const containerOffsets = BFXDomHelper.getParentContainerOffsets(viewElement);

      this.menuPosition.x = event.x - containerOffsets.left;
      this.menuPosition.y = event.y - containerOffsets.top;

      this.showMenu = true;
      setTimeout(() => {
        let height: number = this.contextMenuElement.nativeElement.offsetHeight;
        let width: number = this.contextMenuElement.nativeElement.offsetWidth;
        let maxHeight: number = window.innerHeight;
        let maxWidth: number = window.innerWidth;

        if (height + this.menuPosition.y > maxHeight - 100) {
          this.menuPosition.y = maxHeight - height - 100;
        }

        if (width + this.menuPosition.x > maxWidth) {
          this.menuPosition.x = maxWidth - width;
        }
      }, 0);
    } else {
      let parentWindow: Window = this.element.nativeElement.ownerDocument.defaultView;

      if (parentWindow != null) {
        const documentListenerFn = () => {
          this.menuWindow.hide();
        };

        if (this.documentListener != null) {
          this.documentListener();
        }

        this.documentListener = () => {
          parentWindow.document.removeEventListener("click", documentListenerFn);
        };

        parentWindow.document.addEventListener("click", documentListenerFn);
      }

      this.showMenu = true;
      this.menuWindow
        .getNativeWindow()
        .document.body.appendChild(this.contextMenuElement.nativeElement);

      requestAnimationFrame(
        (() => {
          let x: number = event.screenX,
            y: number = event.screenY,
            width: number = 120 * NativeDesktopSupport.zoomLevel,
            height: number = 255 * NativeDesktopSupport.zoomLevel;
          let menuElement: Element = this.contextMenuElement.nativeElement;

          width =
            menuElement.clientWidth > 0
              ? menuElement.clientWidth * NativeDesktopSupport.zoomLevel
              : width;
          height =
            menuElement.clientHeight > 0
              ? menuElement.clientHeight * NativeDesktopSupport.zoomLevel
              : height;

          let monitorInfo = NativeDesktopSupport.getMonitorInfo();
          let rect: Rect = monitorInfo.virtualScreen;

          let maxX: number = rect.right - 25;
          let maxY: number = rect.bottom - 25;

          if (x + width > maxX) {
            x = maxX - width;
          }

          if (y - height < 0 && y + height > maxY) {
            y = maxY - height;
          }

          if (y + height > maxY) {
            y = y - height;
          }

          this.menuWindow.setBounds(x, y, width, height).then(() => {
            this.menuWindow.show();
          });
        }).bind(this),
      );
    }
  }

  public handleAccept() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        this.hideActions();
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService.accept(orderIds).then((value: any) => {
          logger.out.info(`Accept complete for ${orderIds.length} orders`);
          this.updateGridData();
        });
      }
    }
  }

  public handleReject() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        this.hideActions();
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService.reject(orderIds).then((value: any) => {
          logger.out.info(`Reject complete for ${orderIds.length} orders`);
          this.updateGridData();
        });
      }
    }
  }

  public handleAbort() {
    this.hideMenu();

    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        this.hideActions();
        let orderIds: string[] = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        this.scratchPadService.reject(orderIds, true).then((value: any) => logger.out.info(value));
      }
    }
  }

  protected hideActions(): void {
    this.canAbort =
      this.canAccept =
      this.canAbort =
      this.canSubmit =
      this.canSave =
      this.canApprove =
      this.canReject =
        false;
  }

  /**
   * Works on desktop
   */
  @HostListener("document:click", ["$event"])
  onDocumentClicked(ev) {
    this.showMenu = false;
  }

  public handleImport($event) {
    this.hideMenu();

    this.scratchPadService.notifyImportBusyOnFooter("footer.notification.scratchpad.import.busy");
    this.importBusy = true;

    let file = null;

    if ($event.srcElement && $event.srcElement.files && $event.srcElement.files.length > 0) {
      file = $event.srcElement.files[0];
    } else if ($event.dataTransfer?.files && $event.dataTransfer?.files.length > 0) {
      file = $event.dataTransfer.files[0];
    }

    if (file != null) {
      this.scratchPadService.importOrdersFromFile(file).then(
        (response) => {
          this.scratchPadService.notifyImportCompleteOnFooter();
          this.importBusy = false;
        },
        (reject) => {
          this.scratchPadService.notifyImportFailureOnFooter();
          this.importBusy = false;
        },
      );
    }
  }

  public handleBulk() {
    this.hideMenu();
    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        let selectedIds: Array<string> = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        if (selectedIds.length > 0) {
          this.scratchPadService.bulkOrders(selectedIds).then(
            ((value: any) => {
              this.updateGridData();
            }).bind(this),
          );
        }
      }
    }
  }

  public handleUnbulk() {
    this.hideMenu();
    if (this.gridOptions.api) {
      let rows: any[] = this.gridOptions.api.getSelectedRows();
      if (rows.length > 0) {
        let selectedIds: Array<string> = rows.map((row) => row[COLUMN_EXTERNAL_REF]);
        if (selectedIds.length > 0) {
          this.scratchPadService.unbulkOrders(selectedIds).then(
            ((value: any) => {
              this.updateGridData();
            }).bind(this),
          );
        }
      }
    }
  }

  public restoreFromConfiguration(config: any): void {
    this.columnsConfiguration = config.columnsConfiguration;
    this.sortingModel = config.sortingModel;
  }

  public restoreSortFromConfig(): void {
    if (this.gridOptions.api && this.sortingModel) {
      this.gridOptions.api.setSortModel(this.sortingModel);
    }
  }

  protected updateSelectionHeader(): void {
    if (this.gridOptions.api) {
      this.subjectCheckPermissions.next();
      let col = this.gridOptions.columnApi.getColumn("selection");
      let colDef = col.getColDef();

      colDef.headerName = "(" + this.selectedRowsLength() + ")";
      colDef.headerTooltip = colDef.headerName;

      this.gridOptions.api.refreshHeader();
      clearTimeout(this.myHeaderSelectionTimer);
      this.myHeaderSelectionTimer = undefined;
    }
  }

  protected onPartialUpdateEvent(rows: Array<any>): void {
    if (this.gridOptions.api) {
      this.gridOptions.api.batchUpdateRowData({ update: rows });
    }
  }

  protected applyTooltipValueGetter(
    col: BfxColDef,
    tooltipValueGetter = GenericTooltipValueGetter,
  ): void {
    if (col.tooltipValueGetter == null && col.showTooltip !== false) {
      col.tooltipValueGetter = tooltipValueGetter;
    }
  }

  public onFileDragLeave(event) {
    this.canDropFile = false;
    event.preventDefault();
  }

  public onFileDragged(event) {
    this.canDropFile = !this.importBusy;
    event.preventDefault();
  }

  public onFileDropped(event) {
    this.canDropFile = false;

    if (this.importBusy) {
      this.scratchPadService.showFailedDialog(
        this.translationService.instant("scratchpad.import.busy"),
      );
    } else if (this.checkDropEvent(event)) {
      this.handleImport(event);
    } else if (event.dataTransfer?.files && event.dataTransfer?.files.length > 1) {
      this.scratchPadService.showFailedDialog(
        this.translationService.instant("scratchpad.multiple.files.not.allowed"),
      );
    }

    event.preventDefault();
  }

  protected checkDropEvent(event): boolean {
    return event.dataTransfer?.files && event.dataTransfer?.files.length == 1;
  }
}
